import { computed } from 'vue'

import { useSessionStorage, watchOnce } from '@vueuse/core'

import { type Product, type ProductVariant } from '@/types'

import { type WishlistStoreItem, useWishlistStore } from '../useWishlistStore'

/**
 * 
 * reactive useWishlist()
 * 
 * @returns the cart, the cart identifyer and useful cart add and remove methods
 * @notes The cart is highly coupled with the wishlistStore composable.
 * 
 */
export const useWishlist = () => {
  const wishlistStore = useWishlistStore()

  const wishlistItems = useSessionStorage("wishlist-items", [] as WishlistStoreItem[])

  watchOnce(wishlistItems, () => {
    wishlistStore.syncWishlistItemsFromLocalStorage(wishlistItems.value)
  }, {
    immediate: true
  })

  const isInWishlist = (productVariantId: string) => {
    return wishlistItems.value.find((item: WishlistStoreItem) => item.id === productVariantId)
  }

  const addProductToWishlist = async (product: Product, productVariant: ProductVariant, quantity: number) => {
    // Add The Product To The Store:
    wishlistStore.addProductToWishlist(productVariant.id, product, productVariant, quantity)

    wishlistItems.value = wishlistStore.getAllItems
  }

  const removeProductFromWishlist = async (product: Product, productVariant: ProductVariant, quantity: number) => {
    // Remove The Product From The Store:
    wishlistStore.removeProductFromWishlist(productVariant.id, product, productVariant, quantity)

    wishlistItems.value = wishlistStore.getAllItems
  }

  const updateProductInWishlist = async (product: Product, productVariant: ProductVariant, quantity: number) => {
    // Update The Product In The Store:
    wishlistStore.updateProductInWishlist(productVariant.id, productVariant, quantity)

    wishlistItems.value = wishlistStore.getAllItems
  }

  const resetWishlist = async () => {
    // Reset the wishlist in the store:
    wishlistStore.resetWishlist()
    // Reset the wishlist line items to blank array:
    wishlistItems.value = []
  }

  return {
    isInWishlist,
    addProductToWishlist,
    removeProductFromWishlist,
    updateProductInWishlist,
    resetWishlist,
    wishlistStore,
    wishlistItems,
    wishlistTotal: computed(() => wishlistStore.total)
  }
}